// AdminInventoryCount: Scan products to build a physical count, then generate adjustment transactions
import React, { useState } from "react";
import {
	Box,
	Typography,
	Table,
	TableHead,
	TableRow,
	TableCell,
	TableBody,
	Button,
	Chip,
	Alert,
	CircularProgress,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Paper,
} from "@mui/material";
import BarcodeScanner from "../../../Components/BarcodeScanner";
import supabase from "../../../supabaseClient";

export default function AdminInventoryCount() {
	const [counting, setCounting] = useState(false);
	const [counts, setCounts] = useState({}); // { store_item_id: { title, sku, scanned, system_qty } }
	const [scanError, setScanError] = useState("");
	const [reviewOpen, setReviewOpen] = useState(false);
	const [applying, setApplying] = useState(false);
	const [applyError, setApplyError] = useState("");
	const [applySuccess, setApplySuccess] = useState(false);

	const handleScan = async (value) => {
		setScanError("");
		// Lookup by barcode then SKU
		let item = null;
		const { data: byBarcode } = await supabase
			.from("store_items")
			.select("id, title, sku, barcode, quantity_on_hand")
			.eq("barcode", value)
			.eq("active", true)
			.maybeSingle();
		if (byBarcode) {
			item = byBarcode;
		} else {
			const { data: bySku } = await supabase
				.from("store_items")
				.select("id, title, sku, barcode, quantity_on_hand")
				.eq("sku", value)
				.eq("active", true)
				.maybeSingle();
			item = bySku;
		}

		if (!item) {
			setScanError(`Unknown barcode/SKU: "${value}"`);
			return;
		}

		setCounts((prev) => {
			const existing = prev[item.id];
			return {
				...prev,
				[item.id]: {
					title: item.title,
					sku: item.sku || "",
					barcode: item.barcode || "",
					system_qty: item.quantity_on_hand,
					scanned: (existing?.scanned ?? 0) + 1,
				},
			};
		});
	};

	const adjustments = Object.entries(counts).map(([id, c]) => ({
		store_item_id: id,
		title: c.title,
		sku: c.sku,
		system_qty: c.system_qty,
		counted_qty: c.scanned,
		diff: c.scanned - c.system_qty,
	}));

	const applyAdjustments = async () => {
		setApplying(true);
		setApplyError("");
		const { data: { user } } = await supabase.auth.getUser();
		const toAdjust = adjustments.filter((a) => a.diff !== 0);

		for (const adj of toAdjust) {
			const before = adj.system_qty;
			const after = adj.counted_qty;

			const { error: updateErr } = await supabase
				.from("store_items")
				.update({ quantity_on_hand: after })
				.eq("id", adj.store_item_id);
			if (updateErr) { setApplyError(updateErr.message); setApplying(false); return; }

			const { error: txErr } = await supabase
				.from("inventory_transactions")
				.insert([{
					store_item_id: adj.store_item_id,
					transaction_type: "adjustment",
					quantity_change: adj.diff,
					quantity_before: before,
					quantity_after: after,
					notes: "Physical inventory count",
					created_by: user?.id,
				}]);
			if (txErr) { setApplyError(txErr.message); setApplying(false); return; }
		}

		setApplying(false);
		setApplySuccess(true);
		setReviewOpen(false);
		setCounts({});
		setCounting(false);
	};

	if (applySuccess) {
		return (
			<Box>
				<Alert severity="success" sx={{ mb: 2 }}>
					Inventory count applied successfully. Stock levels have been updated.
				</Alert>
				<Button variant="outlined" onClick={() => setApplySuccess(false)}>
					Start New Count
				</Button>
			</Box>
		);
	}

	return (
		<Box>
			<Typography variant="h5" gutterBottom>
				Inventory Count
			</Typography>
			<Typography variant="body2" color="text.secondary" mb={2}>
				Scan each product to count it. Scan the same item multiple times to increment
				the count. When finished, review and apply adjustments.
			</Typography>

			{!counting ? (
				<Button variant="contained" onClick={() => { setCounting(true); setCounts({}); }}>
					Start New Count
				</Button>
			) : (
				<Box>
					<Paper sx={{ p: 2, mb: 2, maxWidth: 500 }}>
						<Typography variant="subtitle2" gutterBottom>
							Scan items
						</Typography>
						<BarcodeScanner onScan={handleScan} placeholder="Scan barcode or enter SKU…" />
						{scanError && (
							<Alert severity="warning" sx={{ mt: 1 }} onClose={() => setScanError("")}>
								{scanError}
							</Alert>
						)}
					</Paper>

					{Object.keys(counts).length > 0 && (
						<Box>
							<Typography variant="subtitle1" gutterBottom>
								Items Counted ({Object.keys(counts).length})
							</Typography>
							<Box sx={{ overflowX: "auto" }}>
								<Table size="small" sx={{ minWidth: 500 }}>
									<TableHead>
										<TableRow>
											<TableCell>Title</TableCell>
											<TableCell>SKU</TableCell>
											<TableCell align="center">Scanned</TableCell>
											<TableCell align="center">System Qty</TableCell>
											<TableCell align="center">Difference</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{Object.entries(counts).map(([id, c]) => {
											const diff = c.scanned - c.system_qty;
											return (
												<TableRow key={id}>
													<TableCell>{c.title}</TableCell>
													<TableCell>{c.sku || "—"}</TableCell>
													<TableCell align="center">{c.scanned}</TableCell>
													<TableCell align="center">{c.system_qty}</TableCell>
													<TableCell align="center">
														{diff === 0 ? (
															<Chip label="Match" color="success" size="small" />
														) : (
															<Chip
																label={diff > 0 ? `+${diff}` : `${diff}`}
																color={diff > 0 ? "info" : "warning"}
																size="small"
															/>
														)}
													</TableCell>
												</TableRow>
											);
										})}
									</TableBody>
								</Table>
							</Box>

							<Box display="flex" gap={2} mt={2}>
								<Button
									variant="contained"
									onClick={() => setReviewOpen(true)}
								>
									Review & Apply
								</Button>
								<Button
									variant="outlined"
									color="error"
									onClick={() => { setCounts({}); setCounting(false); }}
								>
									Cancel Count
								</Button>
							</Box>
						</Box>
					)}
				</Box>
			)}

			{/* Review dialog */}
			<Dialog open={reviewOpen} onClose={() => setReviewOpen(false)} maxWidth="md" fullWidth>
				<DialogTitle>Review Inventory Adjustments</DialogTitle>
				<DialogContent>
					<Typography variant="body2" color="text.secondary" mb={2}>
						The following adjustments will be applied to bring inventory in line with your count.
						Items with no difference will be skipped.
					</Typography>
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell>Title</TableCell>
								<TableCell align="center">Counted</TableCell>
								<TableCell align="center">System</TableCell>
								<TableCell align="center">Adjustment</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{adjustments.map((adj) => (
								<TableRow key={adj.store_item_id} sx={{ opacity: adj.diff === 0 ? 0.5 : 1 }}>
									<TableCell>{adj.title}</TableCell>
									<TableCell align="center">{adj.counted_qty}</TableCell>
									<TableCell align="center">{adj.system_qty}</TableCell>
									<TableCell align="center">
										{adj.diff === 0 ? (
											<Typography variant="body2" color="text.secondary">No change</Typography>
										) : (
											<Typography
												variant="body2"
												fontWeight="bold"
												color={adj.diff > 0 ? "success.main" : "error.main"}
											>
												{adj.diff > 0 ? "+" : ""}{adj.diff}
											</Typography>
										)}
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
					{applyError && <Alert severity="error" sx={{ mt: 2 }}>{applyError}</Alert>}
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setReviewOpen(false)} disabled={applying}>Cancel</Button>
					<Button
						onClick={applyAdjustments}
						variant="contained"
						color="primary"
						disabled={applying || adjustments.every((a) => a.diff === 0)}
					>
						{applying ? <CircularProgress size={20} /> : "Apply Adjustments"}
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
}
